import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Input,
  Select,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  IconButton,
  useColorModeValue,
  Alert,
  AlertIcon,
  Tooltip,
  Switch,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Textarea,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, ViewIcon, EditIcon } from "@chakra-ui/icons";
import { FiRefreshCw, FiPlus, FiMinus } from "react-icons/fi";
import { automationService } from "../services/edosService";

const TRIGGER_OPTIONS = [
  "ticket.created",
  "ticket.status_changed",
  "ticket.assigned",
  "sprint.started",
  "sprint.completed",
  "project.status_changed",
  "milestone.overdue",
];
const ACTION_TYPES = [
  "send_email",
  "update_ticket_status",
  "assign_ticket",
  "add_comment",
  "update_project_status",
  "create_ticket",
  "notify_team",
];
const OPERATORS = ["eq", "neq", "gt", "lt", "in", "nin", "contains"];

const STATUS_COLORS = { active: "green", inactive: "gray", paused: "yellow" };

const emptyCondition = () => ({ field: "", operator: "eq", value: "" });
const emptyAction = () => ({ actionType: "send_email", params: {} });

export default function AutomationsPage() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDetailOpen,
    onOpen: onDetailOpen,
    onClose: onDetailClose,
  } = useDisclosure();
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("gray.50", "gray.700");

  const [automations, setAutomations] = useState([]);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    trigger: "ticket.created",
    conditions: [],
    actions: [emptyAction()],
    isActive: true,
  });

  const fetchAutomations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await automationService.list();
      setAutomations(res.data.data || res.data.automations || []);
    } catch (err) {
      toast({ title: "Failed to load automations", status: "error" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchAutomations();
  }, [fetchAutomations]);

  const handleOpenCreate = () => {
    setEditMode(false);
    setEditId(null);
    setForm({
      name: "",
      trigger: "ticket.created",
      conditions: [],
      actions: [emptyAction()],
      isActive: true,
    });
    onOpen();
  };

  const handleOpenEdit = (a) => {
    setEditMode(true);
    setEditId(a._id);
    setForm({
      name: a.name,
      trigger: a.trigger,
      conditions: a.conditions?.length ? a.conditions : [],
      actions: a.actions?.length ? a.actions : [emptyAction()],
      isActive: a.isActive !== false,
    });
    onOpen();
  };

  const handleSubmit = async () => {
    if (!form.name || !form.trigger || !form.actions.length) {
      toast({
        title: "Name, trigger, and at least one action are required",
        status: "warning",
      });
      return;
    }
    setSubmitting(true);
    try {
      if (editMode) {
        await automationService.update(editId, form);
        toast({ title: "Automation updated", status: "success" });
      } else {
        await automationService.create(form);
        toast({ title: "Automation created", status: "success" });
      }
      onClose();
      fetchAutomations();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed",
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleView = async (a) => {
    setSelectedAutomation(null);
    onDetailOpen();
    try {
      const res = await automationService.getById(a._id);
      setSelectedAutomation(res.data.data || res.data.automation || res.data);
    } catch (err) {
      toast({ title: "Failed to load details", status: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this automation?")) return;
    try {
      await automationService.delete(id);
      toast({ title: "Automation deleted", status: "success" });
      fetchAutomations();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed",
        status: "error",
      });
    }
  };

  // Condition helpers
  const addCondition = () =>
    setForm((f) => ({ ...f, conditions: [...f.conditions, emptyCondition()] }));
  const removeCondition = (i) =>
    setForm((f) => ({
      ...f,
      conditions: f.conditions.filter((_, idx) => idx !== i),
    }));
  const updateCondition = (i, key, val) =>
    setForm((f) => ({
      ...f,
      conditions: f.conditions.map((c, idx) =>
        idx === i ? { ...c, [key]: val } : c,
      ),
    }));

  // Action helpers
  const addAction = () =>
    setForm((f) => ({ ...f, actions: [...f.actions, emptyAction()] }));
  const removeAction = (i) =>
    setForm((f) => ({
      ...f,
      actions: f.actions.filter((_, idx) => idx !== i),
    }));
  const updateAction = (i, key, val) =>
    setForm((f) => ({
      ...f,
      actions: f.actions.map((a, idx) =>
        idx === i ? { ...a, [key]: val } : a,
      ),
    }));

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold">
            Automations
          </Text>
          <Text color="gray.500" fontSize="sm">
            Configure workflow automation rules and triggers
          </Text>
        </Box>
        <HStack>
          <IconButton
            icon={<FiRefreshCw />}
            variant="outline"
            aria-label="Refresh"
            onClick={fetchAutomations}
          />
          <Button
            leftIcon={<AddIcon />}
            colorScheme="pink"
            onClick={handleOpenCreate}
          >
            New Automation
          </Button>
        </HStack>
      </Flex>

      <Box
        bg={bg}
        border="1px"
        borderColor={borderColor}
        borderRadius="md"
        overflow="hidden"
      >
        {loading ? (
          <Flex justify="center" p={10}>
            <Spinner size="xl" color="pink.500" />
          </Flex>
        ) : automations.length === 0 ? (
          <Alert status="info" m={4}>
            <AlertIcon />
            No automation rules yet.
          </Alert>
        ) : (
          <Table variant="simple" size="sm">
            <Thead bg={headerBg}>
              <Tr>
                <Th>Name</Th>
                <Th>Trigger</Th>
                <Th>Conditions</Th>
                <Th>Actions</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {automations.map((a) => (
                <Tr key={a._id}>
                  <Td>
                    <Text fontWeight="semibold" fontSize="sm">
                      {a.name}
                    </Text>
                  </Td>
                  <Td>
                    <Badge colorScheme="purple" fontSize="xs">
                      {a.trigger}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge>{a.conditions?.length || 0} conditions</Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme="blue">
                      {a.actions?.length || 0} actions
                    </Badge>
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={
                        STATUS_COLORS[a.isActive ? "active" : "inactive"]
                      }
                    >
                      {a.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <Tooltip label="View">
                        <IconButton
                          size="xs"
                          icon={<ViewIcon />}
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => handleView(a)}
                        />
                      </Tooltip>
                      <Tooltip label="Edit">
                        <IconButton
                          size="xs"
                          icon={<EditIcon />}
                          variant="ghost"
                          colorScheme="orange"
                          onClick={() => handleOpenEdit(a)}
                        />
                      </Tooltip>
                      <Tooltip label="Delete">
                        <IconButton
                          size="xs"
                          icon={<DeleteIcon />}
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDelete(a._id)}
                        />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* Create / Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editMode ? "Edit Automation" : "Create Automation"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={5} align="stretch">
              <HStack>
                <FormControl isRequired flex={2}>
                  <FormLabel>Automation Name</FormLabel>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Notify on ticket creation"
                  />
                </FormControl>
                <FormControl flex={1}>
                  <FormLabel>Active</FormLabel>
                  <Switch
                    isChecked={form.isActive}
                    onChange={(e) =>
                      setForm({ ...form, isActive: e.target.checked })
                    }
                    colorScheme="green"
                  />
                </FormControl>
              </HStack>

              <FormControl isRequired>
                <FormLabel>Trigger Event</FormLabel>
                <Select
                  value={form.trigger}
                  onChange={(e) =>
                    setForm({ ...form, trigger: e.target.value })
                  }
                >
                  {TRIGGER_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {/* Conditions */}
              <Box
                border="1px"
                borderColor={borderColor}
                borderRadius="md"
                p={3}
              >
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontWeight="semibold" fontSize="sm">
                    Conditions (optional)
                  </Text>
                  <Button
                    size="xs"
                    leftIcon={<FiPlus />}
                    onClick={addCondition}
                  >
                    Add Condition
                  </Button>
                </Flex>
                <VStack spacing={2} align="stretch">
                  {form.conditions.map((c, i) => (
                    <HStack key={i} spacing={2}>
                      <Input
                        size="sm"
                        placeholder="Field (e.g. status)"
                        value={c.field}
                        onChange={(e) =>
                          updateCondition(i, "field", e.target.value)
                        }
                      />
                      <Select
                        size="sm"
                        value={c.operator}
                        onChange={(e) =>
                          updateCondition(i, "operator", e.target.value)
                        }
                        w="120px"
                      >
                        {OPERATORS.map((op) => (
                          <option key={op} value={op}>
                            {op}
                          </option>
                        ))}
                      </Select>
                      <Input
                        size="sm"
                        placeholder="Value"
                        value={c.value}
                        onChange={(e) =>
                          updateCondition(i, "value", e.target.value)
                        }
                      />
                      <IconButton
                        size="xs"
                        icon={<FiMinus />}
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => removeCondition(i)}
                      />
                    </HStack>
                  ))}
                  {form.conditions.length === 0 && (
                    <Text fontSize="xs" color="gray.400">
                      No conditions — automation always fires on trigger
                    </Text>
                  )}
                </VStack>
              </Box>

              {/* Actions */}
              <Box
                border="1px"
                borderColor={borderColor}
                borderRadius="md"
                p={3}
              >
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontWeight="semibold" fontSize="sm">
                    Actions
                  </Text>
                  <Button size="xs" leftIcon={<FiPlus />} onClick={addAction}>
                    Add Action
                  </Button>
                </Flex>
                <VStack spacing={3} align="stretch">
                  {form.actions.map((a, i) => (
                    <HStack key={i} align="start" spacing={2}>
                      <Select
                        size="sm"
                        value={a.actionType}
                        onChange={(e) =>
                          updateAction(i, "actionType", e.target.value)
                        }
                        w="200px"
                      >
                        {ACTION_TYPES.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </Select>
                      <Textarea
                        size="sm"
                        placeholder='Params as JSON, e.g. {"to":"manager@acme.com"}'
                        value={
                          typeof a.params === "object"
                            ? JSON.stringify(a.params)
                            : a.params
                        }
                        onChange={(e) => {
                          let parsed;
                          try {
                            parsed = JSON.parse(e.target.value);
                          } catch {
                            parsed = e.target.value;
                          }
                          updateAction(i, "params", parsed);
                        }}
                        rows={2}
                        flex={1}
                      />
                      {form.actions.length > 1 && (
                        <IconButton
                          size="xs"
                          icon={<FiMinus />}
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => removeAction(i)}
                        />
                      )}
                    </HStack>
                  ))}
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="pink"
              isLoading={submitting}
              onClick={handleSubmit}
            >
              {editMode ? "Update" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Detail Modal */}
      <Modal isOpen={isDetailOpen} onClose={onDetailClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedAutomation?.name || "Automation Detail"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {!selectedAutomation ? (
              <Flex justify="center" p={6}>
                <Spinner />
              </Flex>
            ) : (
              <VStack spacing={4} align="stretch">
                <HStack>
                  <Badge colorScheme="purple">
                    {selectedAutomation.trigger}
                  </Badge>
                  <Badge
                    colorScheme={selectedAutomation.isActive ? "green" : "gray"}
                  >
                    {selectedAutomation.isActive ? "Active" : "Inactive"}
                  </Badge>
                </HStack>
                {selectedAutomation.conditions?.length > 0 && (
                  <Box>
                    <Text fontWeight="semibold" mb={1}>
                      Conditions
                    </Text>
                    {selectedAutomation.conditions.map((c, i) => (
                      <Text
                        key={i}
                        fontSize="sm"
                        fontFamily="mono"
                        bg={headerBg}
                        p={1}
                        borderRadius="sm"
                        mb={1}
                      >
                        {c.field} {c.operator} {JSON.stringify(c.value)}
                      </Text>
                    ))}
                  </Box>
                )}
                <Box>
                  <Text fontWeight="semibold" mb={1}>
                    Actions
                  </Text>
                  {selectedAutomation.actions?.map((a, i) => (
                    <Box key={i} bg={headerBg} p={2} borderRadius="md" mb={1}>
                      <Badge colorScheme="blue" mb={1}>
                        {a.actionType}
                      </Badge>
                      <Text fontSize="xs" fontFamily="mono">
                        {JSON.stringify(a.params, null, 2)}
                      </Text>
                    </Box>
                  ))}
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onDetailClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
